'use client';

import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import gsap from 'gsap';
import '../CSS/ScrollExpandMedia.css';

const LERP = 0.14;
const WHEEL_SENSITIVITY = 0.0011;
const TOUCH_SENSITIVITY = 0.006;
const TITLE_FADE_END = 0.32;
const PORTAL_MOUNT_AT = 0.28;
const PORTAL_CONTENT_AT = 0.42;
const EXPANDED_AT = 0.98;

const ScrollExpandMedia = forwardRef(function ScrollExpandMedia(
  {
    titleLeft,
    titleRight,
    children,
    renderBackground,
    landingOverlay,
    portalContent,
    expandedPortalContent,
    onProgressChange,
  },
  ref
) {
  const rootRef = useRef(null);
  const progressRef = useRef({ value: 0 });
  const targetRef = useRef(0);
  const tweenRef = useRef(null);
  const onProgressChangeRef = useRef(onProgressChange);
  const mediaFullyExpandedRef = useRef(false);

  const [showContent, setShowContent] = useState(false);
  const [mediaFullyExpanded, setMediaFullyExpanded] = useState(false);
  const [portalMounted, setPortalMounted] = useState(false);
  const [portalContentVisible, setPortalContentVisible] = useState(false);
  const [titleVisible, setTitleVisible] = useState(true);
  const touchStartYRef = useRef(0);
  const lastReportedProgressRef = useRef(-1);

  useEffect(() => {
    onProgressChangeRef.current = onProgressChange;
  }, [onProgressChange]);

  const syncThresholdState = useCallback((p) => {
    const expanded = p >= EXPANDED_AT;
    const show = p >= EXPANDED_AT;
    const mountPortal = p >= PORTAL_MOUNT_AT;
    const showPortalContent = p >= PORTAL_CONTENT_AT;
    const showTitle = p < TITLE_FADE_END;

    if (mediaFullyExpandedRef.current !== expanded) {
      mediaFullyExpandedRef.current = expanded;
      setMediaFullyExpanded(expanded);
    }
    setShowContent((prev) => (prev !== show ? show : prev));
    setPortalMounted((prev) => (prev !== mountPortal ? mountPortal : prev));
    setPortalContentVisible((prev) =>
      prev !== showPortalContent ? showPortalContent : prev
    );
    setTitleVisible((prev) => (prev !== showTitle ? showTitle : prev));

    if (onProgressChangeRef.current) {
      const bucket = Math.floor(p * 25);
      const lastBucket = Math.floor(lastReportedProgressRef.current * 25);
      if (
        bucket !== lastBucket ||
        p <= 0.001 ||
        p >= EXPANDED_AT
      ) {
        lastReportedProgressRef.current = p;
        onProgressChangeRef.current(p);
      }
    }
  }, []);

  const applyProgressToDOM = useCallback(
    (p) => {
      const root = rootRef.current;
      if (!root) return;
      const mobile = window.innerWidth < 768;
      root.style.setProperty('--se-progress', String(p));
      root.style.setProperty('--se-text-shift', `${p * (mobile ? 38 : 26)}vw`);
      const innerScale =
        p < PORTAL_MOUNT_AT
          ? 0
          : Math.min(1, 0.12 + ((p - PORTAL_MOUNT_AT) / (1 - PORTAL_MOUNT_AT)) * 0.88);
      root.style.setProperty('--se-inner-scale', String(innerScale));
      root.dataset.expanded = p >= EXPANDED_AT ? 'true' : 'false';
      syncThresholdState(p);
    },
    [syncThresholdState]
  );

  const setTarget = useCallback(
    (next) => {
      const clamped = Math.min(Math.max(next, 0), 1);
      targetRef.current = clamped;
      if (tweenRef.current) {
        tweenRef.current.kill();
        tweenRef.current = null;
      }
    },
    []
  );

  const animateTo = useCallback(
    (value, duration = 0.85) => {
      if (tweenRef.current) tweenRef.current.kill();
      targetRef.current = value;
      tweenRef.current = gsap.to(progressRef.current, {
        value,
        duration,
        ease: 'power2.inOut',
        overwrite: true,
        onUpdate: () => applyProgressToDOM(progressRef.current.value),
        onComplete: () => {
          tweenRef.current = null;
          applyProgressToDOM(value);
        },
      });
    },
    [applyProgressToDOM]
  );

  useImperativeHandle(ref, () => ({
    completeTransition: () => animateTo(1, 0.9),
    resetTransition: () => animateTo(0, 0.75),
  }));

  useEffect(() => {
    applyProgressToDOM(0);
    targetRef.current = 0;
    progressRef.current.value = 0;

    const tick = () => {
      if (tweenRef.current) return;
      const current = progressRef.current.value;
      const target = targetRef.current;
      const diff = target - current;
      if (Math.abs(diff) < 0.0004) {
        if (current !== target) {
          progressRef.current.value = target;
          applyProgressToDOM(target);
        }
        return;
      }
      progressRef.current.value = current + diff * LERP;
      applyProgressToDOM(progressRef.current.value);
    };

    gsap.ticker.add(tick);

    const handleWheel = (e) => {
      if (mediaFullyExpandedRef.current && e.deltaY < 0 && window.scrollY <= 5) {
        setTarget(0.92);
        e.preventDefault();
      } else if (!mediaFullyExpandedRef.current) {
        e.preventDefault();
        setTarget(targetRef.current + e.deltaY * WHEEL_SENSITIVITY);
      }
    };

    const handleTouchStart = (e) => {
      touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const touchStartY = touchStartYRef.current;
      if (!touchStartY) return;

      const touchY = e.touches[0].clientY;
      const deltaY = touchStartY - touchY;

      if (mediaFullyExpandedRef.current && deltaY < -20 && window.scrollY <= 5) {
        setTarget(0.92);
        e.preventDefault();
      } else if (!mediaFullyExpandedRef.current) {
        e.preventDefault();
        const factor = deltaY < 0 ? TOUCH_SENSITIVITY * 1.4 : TOUCH_SENSITIVITY;
        setTarget(targetRef.current + deltaY * factor);
        touchStartYRef.current = touchY;
      }
    };

    const handleTouchEnd = () => {
      touchStartYRef.current = 0;
    };

    const handleScroll = () => {
      if (!mediaFullyExpandedRef.current) {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      gsap.ticker.remove(tick);
      if (tweenRef.current) tweenRef.current.kill();
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [applyProgressToDOM, setTarget]);

  return (
    <div
      ref={rootRef}
      className="scroll-expand-media"
      style={{ '--se-progress': 0, '--se-text-shift': '0vw', '--se-inner-scale': 0 }}
    >
      <section className="scroll-expand-section">
        <div className="scroll-expand-inner">
          <div className="scroll-expand-bg">
            {renderBackground}
          </div>

          {landingOverlay && !showContent && (
            <div className="scroll-expand-landing-overlay">
              <div className="scroll-expand-landing-overlay-inner">
                {landingOverlay}
              </div>
            </div>
          )}

          <div className="scroll-expand-container">
            <div
              className={`scroll-expand-hero ${
                mediaFullyExpanded ? 'scroll-expand-hero--done' : ''
              }`}
            >
              {(titleLeft || titleRight) && titleVisible && !mediaFullyExpanded && (
                <div className="scroll-expand-title-wrap scroll-expand-title-wrap--stacked">
                  {titleLeft && (
                    <h2 className="scroll-expand-title scroll-expand-title--line1">
                      {titleLeft}
                    </h2>
                  )}
                  {titleRight && (
                    <h2 className="scroll-expand-title scroll-expand-title--line2">
                      {titleRight}
                    </h2>
                  )}
                </div>
              )}

              <div className="scroll-expand-media-box scroll-expand-media-box--portal">
                {portalMounted && !mediaFullyExpanded && (
                  <div className="scroll-expand-portal">
                    <div
                      className={`scroll-expand-portal-viewport ${
                        portalContentVisible ? 'is-content-visible' : ''
                      }`}
                    >
                      {portalContent}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {mediaFullyExpanded && (expandedPortalContent || portalContent) && (
              <div className="scroll-expand-expanded-home">
                {expandedPortalContent || portalContent}
              </div>
            )}

            <section
              className={`scroll-expand-content ${
                showContent ? 'is-visible' : ''
              }`}
            >
              {children}
            </section>
          </div>
        </div>
      </section>
    </div>
  );
});

export default ScrollExpandMedia;
